import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Video, Mail, MessageSquare, ArrowRight, User } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const FORMSPREE_URL = 'https://formspree.io/f/xykbbpbj';
const CALENDLY_URL = 'https://calendly.com/tegosdigital/30min';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const services = [
  'Web Design',
  'Website Redesign',
  'Branding & Logo',
  'SEO Optimization',
  'Social Media Management',
  'AI Receptionist & Automation',
  'Content Creation',
  'Email Marketing',
  'Full Digital Strategy',
  'Other',
];

const isPastOrWeekend = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = date.getDay();
  return date < today || day === 0 || day === 6;
};

export const Quote = () => {
  const [activeTab, setActiveTab] = useState('call');
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [messageForm, setMessageForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBookingChange = (e) => {
    setBookingForm({ ...bookingForm, [e.target.name]: e.target.value });
  };

  const handleMessageChange = (e) => {
    setMessageForm({ ...messageForm, [e.target.name]: e.target.value });
  };

  const handleBookCall = async (e) => {
    e.preventDefault();
    if (!selectedDate) {
      toast.error('Please select a date first.');
      return;
    }
    if (!bookingForm.name || !bookingForm.email) {
      toast.error('Please fill in your name and email.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(FORMSPREE_URL, {
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone || 'Not provided',
        company: bookingForm.company || 'Not provided',
        service: bookingForm.service || 'Not specified',
        message: bookingForm.message || '',
        preferred_date: selectedDate.toLocaleDateString(),
        _subject: `Call Booking Request — ${bookingForm.name}`,
      });
    } catch {
      // Continue to Calendly even if Formspree fails
    }

    const params = new URLSearchParams({
      name: bookingForm.name,
      email: bookingForm.email,
      date: selectedDate.toISOString().split('T')[0],
    });
    window.open(`${CALENDLY_URL}?${params.toString()}`, '_blank', 'noopener,noreferrer');
    toast.success('Opening Calendly — your details are pre-filled!');
    setIsSubmitting(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(FORMSPREE_URL, {
        name: messageForm.name,
        email: messageForm.email,
        phone: messageForm.phone || 'Not provided',
        service: messageForm.service || 'Not specified',
        message: messageForm.message,
        _subject: `New Message — ${messageForm.name}`,
      });
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setMessageForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page" className="min-h-screen bg-[#050505] pt-20">
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Hero */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <motion.span
              variants={itemVariants}
              className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-6"
            >
              Get Started
            </motion.span>
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-tight text-[#F8F9FA] font-['Clash_Display'] mb-6"
            >
              Let's Talk About
              <br />
              <span className="text-gradient-blue">Your Project</span>
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-lg leading-relaxed text-slate-300"
            >
              Schedule a free consultation call or send us a message.
              We'll discuss your goals and how we can help you achieve them.
            </motion.p>
          </motion.div>

          {/* Tab Selector */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="flex justify-center gap-4 mb-12"
          >
            <motion.button
              variants={itemVariants}
              onClick={() => setActiveTab('call')}
              data-testid="tab-schedule-call"
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === 'call'
                  ? 'bg-blue-600 text-white'
                  : 'bg-[#0A0A0A] text-slate-400 border border-[#1F1F22] hover:text-white hover:border-white/20'
              }`}
            >
              <Video size={18} />
              Schedule a Call
            </motion.button>
            <motion.button
              variants={itemVariants}
              onClick={() => setActiveTab('message')}
              data-testid="tab-send-message"
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === 'message'
                  ? 'bg-blue-600 text-white'
                  : 'bg-[#0A0A0A] text-slate-400 border border-[#1F1F22] hover:text-white hover:border-white/20'
              }`}
            >
              <MessageSquare size={18} />
              Send a Message
            </motion.button>
          </motion.div>

          {/* Schedule Call Tab */}
          {activeTab === 'call' && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <form onSubmit={handleBookCall} data-testid="booking-form">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
                  {/* Left — Select Date */}
                  <motion.div
                    variants={itemVariants}
                    className="rounded-2xl bg-[#0A0A0A] border border-[#1F1F22] p-8"
                  >
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                        <CalendarIcon className="text-blue-500" size={20} />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold font-['Clash_Display'] text-lg">Select Date</h3>
                        <p className="text-slate-400 text-sm">Choose your preferred date</p>
                      </div>
                    </div>
                    <div className="flex justify-center" data-testid="date-calendar">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isPastOrWeekend}
                        className="rounded-xl"
                        classNames={{
                          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                          month: "space-y-4",
                          caption: "flex justify-center pt-1 relative items-center",
                          caption_label: "text-sm font-medium text-white",
                          nav: "space-x-1 flex items-center",
                          nav_button: "h-8 w-8 bg-transparent border border-[#1F1F22] rounded-lg p-0 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-colors",
                          nav_button_previous: "absolute left-1",
                          nav_button_next: "absolute right-1",
                          table: "w-full border-collapse",
                          head_row: "flex",
                          head_cell: "text-slate-500 rounded-md w-10 font-normal text-[0.8rem]",
                          row: "flex w-full mt-2",
                          cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:rounded-md",
                          day: "h-10 w-10 p-0 font-normal text-slate-300 hover:bg-[#1F1F22] rounded-lg transition-colors aria-selected:opacity-100",
                          day_selected: "bg-blue-600 text-white hover:bg-blue-500 focus:bg-blue-600 focus:text-white",
                          day_today: "bg-[#1F1F22] text-white",
                          day_outside: "text-slate-600 opacity-50",
                          day_disabled: "text-slate-700 opacity-30",
                          day_hidden: "invisible",
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* Right — Your Details */}
                  <motion.div
                    variants={itemVariants}
                    className="rounded-2xl bg-[#0A0A0A] border border-[#1F1F22] p-8"
                  >
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                        <User className="text-blue-500" size={20} />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold font-['Clash_Display'] text-lg">Your Details</h3>
                        <p className="text-slate-400 text-sm">Tell us about yourself</p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="book-name" className="text-slate-300 text-sm">Name *</Label>
                          <Input
                            id="book-name"
                            name="name"
                            data-testid="book-name-input"
                            value={bookingForm.name}
                            onChange={handleBookingChange}
                            required
                            placeholder="John Doe"
                            className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500 h-11"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="book-email" className="text-slate-300 text-sm">Email *</Label>
                          <Input
                            id="book-email"
                            name="email"
                            type="email"
                            data-testid="book-email-input"
                            value={bookingForm.email}
                            onChange={handleBookingChange}
                            required
                            placeholder="john@company.com"
                            className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500 h-11"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="book-phone" className="text-slate-300 text-sm">Phone</Label>
                          <Input
                            id="book-phone"
                            name="phone"
                            type="tel"
                            data-testid="book-phone-input"
                            value={bookingForm.phone}
                            onChange={handleBookingChange}
                            placeholder="+1 (555) 000-0000"
                            className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500 h-11"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="book-company" className="text-slate-300 text-sm">Company</Label>
                          <Input
                            id="book-company"
                            name="company"
                            data-testid="book-company-input"
                            value={bookingForm.company}
                            onChange={handleBookingChange}
                            placeholder="Your Company"
                            className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500 h-11"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="book-service" className="text-slate-300 text-sm">Service Interested In</Label>
                        <Select value={bookingForm.service} onValueChange={(v) => setBookingForm({ ...bookingForm, service: v })}>
                          <SelectTrigger data-testid="book-service-select" className="bg-[#050505] border-[#1F1F22] text-white focus:border-blue-500 h-11">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-[#1F1F22]">
                            {services.map((s) => (
                              <SelectItem key={s} value={s} className="text-slate-300 focus:bg-blue-600/20 focus:text-white">
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="book-message" className="text-slate-300 text-sm">What would you like to discuss?</Label>
                        <Textarea
                          id="book-message"
                          name="message"
                          data-testid="book-message-input"
                          value={bookingForm.message}
                          onChange={handleBookingChange}
                          placeholder="Tell us about your project, goals, or any questions..."
                          rows={3}
                          className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500 resize-none"
                        />
                      </div>

                      {/* Zoom info */}
                      <div className="flex items-center gap-3 bg-blue-600/10 border border-blue-500/20 rounded-xl px-4 py-3">
                        <Video className="text-blue-400 shrink-0" size={18} />
                        <span className="text-slate-300 text-sm">You'll receive a Zoom meeting link via email after booking</span>
                      </div>

                      <button
                        type="submit"
                        data-testid="book-call-btn"
                        disabled={isSubmitting}
                        className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-4 px-6 rounded-full flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Processing...' : 'Book 15-Min Zoom Call'}
                        <Video size={18} />
                      </button>
                    </div>
                  </motion.div>
                </div>
              </form>
            </motion.div>
          )}

          {/* Send Message Tab */}
          {activeTab === 'message' && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="max-w-2xl mx-auto"
            >
              <motion.form
                variants={itemVariants}
                onSubmit={handleSendMessage}
                data-testid="message-form"
                className="rounded-2xl bg-[#0A0A0A] border border-[#1F1F22] p-8 sm:p-10"
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                    <Mail className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold font-['Clash_Display'] text-lg">Send Us a Message</h3>
                    <p className="text-slate-400 text-sm">We'll get back to you within 24 hours</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="msg-name" className="text-slate-300">Name *</Label>
                      <Input
                        id="msg-name"
                        name="name"
                        data-testid="msg-name-input"
                        value={messageForm.name}
                        onChange={handleMessageChange}
                        required
                        placeholder="John Doe"
                        className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="msg-email" className="text-slate-300">Email *</Label>
                      <Input
                        id="msg-email"
                        name="email"
                        type="email"
                        data-testid="msg-email-input"
                        value={messageForm.email}
                        onChange={handleMessageChange}
                        required
                        placeholder="john@company.com"
                        className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="msg-phone" className="text-slate-300">Phone</Label>
                      <Input
                        id="msg-phone"
                        name="phone"
                        type="tel"
                        data-testid="msg-phone-input"
                        value={messageForm.phone}
                        onChange={handleMessageChange}
                        placeholder="+1 (555) 000-0000"
                        className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="msg-service" className="text-slate-300">Service Interested In</Label>
                      <Select value={messageForm.service} onValueChange={(v) => setMessageForm({ ...messageForm, service: v })}>
                        <SelectTrigger className="bg-[#050505] border-[#1F1F22] text-white focus:border-blue-500">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-[#1F1F22]">
                          {services.map((s) => (
                            <SelectItem key={s} value={s} className="text-slate-300 focus:bg-blue-600/20 focus:text-white">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="msg-message" className="text-slate-300">Message *</Label>
                    <Textarea
                      id="msg-message"
                      name="message"
                      data-testid="msg-message-input"
                      value={messageForm.message}
                      onChange={handleMessageChange}
                      required
                      placeholder="Tell us about your project, timeline, and goals..."
                      rows={5}
                      className="bg-[#050505] border-[#1F1F22] text-white placeholder:text-slate-500 focus:border-blue-500 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="send-message-btn"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-4 px-6 rounded-full flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight size={18} />
                  </button>
                </div>
              </motion.form>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Quote;
